'use client';

import { hashPin } from '@modules/prism-client/lib/manifest/pin-hash';
import {
  getPersistedPinHash,
  setPersistedPinHash,
} from '@modules/prism-client/lib/manifest/pin-storage';
import { useIsWebviewRequest } from '@tuic/lib/src/hooks/use-is-webview-request';
import { twMerge } from '@tuic/tailwind-config';
import { Skeleton } from '@tuic/web-compositions/core/Skeleton';
import { InteractionHeader } from '@tuic/web-compositions/my-trips/InteractionHeader';
import {
  ManifestOverview,
  type ManifestOverviewProps,
} from '@tuic/web-compositions/my-trips/ManifestOverview';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import type { ComponentBlock } from '@modules/prism-client/types/types';
import type { FC } from 'react';

export type ManifestOverviewBlockProps = Omit<ManifestOverviewProps, 'i18n'> & {
  i18n?: NonNullable<ManifestOverviewProps['i18n']> & {
    headerTitle: string;
    headerBack: string;
  };
};

const ManifestOverviewHeader: FC<{ title?: string; backLabel?: string }> = ({
  title,
  backLabel,
}) => {
  const pathname = usePathname() ?? '';
  const isWebview = useIsWebviewRequest();
  const backHref =
    '/' + pathname.split('/').filter(Boolean).slice(0, -1).join('/');

  if (isWebview) {
    return null;
  }

  return (
    <div className="sticky top-0 z-10 mb-8 bg-section-gray md:mb-4 xl:mb-8">
      <InteractionHeader
        title={title ?? <Skeleton className="h-2 w-[150px]" />}
        leftButton={{
          iconName: 'arrow-left',
          variant: 'secondary',
          label: backLabel,
          children: <Link href={backHref || '/'} />,
        }}
      />
    </div>
  );
};

export const ManifestOverviewBlock: ComponentBlock<
  ManifestOverviewBlockProps
> = ({ data, fallback }) => {
  const isWebview = useIsWebviewRequest();

  const isPinRequired = (travellerId: string): boolean => {
    const traveller = data?.travellers?.find(t => t.id === travellerId);

    if (!traveller?.pinHash || !data) return false;

    const persistedPinHash = getPersistedPinHash(traveller.mpi);

    return traveller.pinHash !== persistedPinHash;
  };

  const handlePinEnter = (travellerId: string, enteredPin: string): boolean => {
    const traveller = data?.travellers?.find(t => t.id === travellerId);
    if (!traveller?.pinHash) return false;

    const enteredHash = hashPin(enteredPin.toUpperCase());
    if (traveller.pinHash !== enteredHash) return false;

    setPersistedPinHash(traveller.mpi, enteredHash);

    return true;
  };

  // pt-6 replaces the spacing the hidden in-page header used to provide.
  return (
    <div className={twMerge(isWebview && 'pt-6')}>
      <ManifestOverviewHeader
        title={data?.i18n?.headerTitle}
        backLabel={data?.i18n?.headerBack}
      />
      <ManifestOverview
        {...data}
        fallback={fallback ?? data?.fallback}
        isPinRequired={isPinRequired}
        onPinEnter={handlePinEnter}
      />
    </div>
  );
};
